import { FC, useMemo } from 'react'
import { Sparkles, Brain, Wrench, CheckCircle2, AlertCircle, Pencil } from 'lucide-react'
import { useChat, ToolCallStatus } from '../../contexts/ChatContext'
import { Badge } from '../ui/badge'
import { cn } from '../../lib/utils'

/**
 * The streaming state this panel renders. The regular chat sources these from the
 * global chat context (via the StreamingProgressPanel wrapper below). The case panel —
 * whose streaming state lives per-agent in `agentSessions`, not the global context —
 * passes them explicitly to StreamingProgressPanelView so it renders the identical
 * indicator with NO dependency on ChatContext.
 *
 * Local (not exported): the case panel passes a structurally-typed object literal, so
 * no named import is needed at the call site.
 */
interface StreamingProgressData {
  toolCalls: ToolCallStatus[]
  isThinking: boolean
  isGenerating: boolean
  isStreaming: boolean
}

/**
 * Streaming Progress Panel (view) - Clinical Intelligence Working State
 *
 * A polished indicator that shows the AI is working, aligned with assistant messages.
 * Pure presentational variant: every field arrives via props, so it has NO dependency
 * on ChatContext and is safe to render anywhere (the case panel feeds it per-agent
 * state). The regular chat uses the StreamingProgressPanel wrapper below.
 */
export const StreamingProgressPanelView: FC<StreamingProgressData> = ({
  toolCalls,
  isThinking,
  isGenerating,
  isStreaming,
}) => {
  // Generate stable random heights for neural wave animation
  const waveHeights = useMemo(() =>
    Array.from({ length: 12 }, () => Math.random() * 8 + 3),
    []
  )

  // Don't render if not generating
  if (!isGenerating) {
    return null
  }

  // Determine the current visual state
  const hasToolCalls = toolCalls.length > 0
  const hasActiveToolCall = toolCalls.some(tc => tc.status === 'running')
  const hasStreamingContent = isStreaming

  // Phase label and icon logic. NOTE: the pulse-ring `ring` class MUST be a full literal
  // string — Tailwind's JIT scanner only emits classes it sees verbatim at build time, so an
  // interpolated `bg-${color}/30` is silently stripped and the halo renders colorless in prod
  // (the deleted CasePanelProgressPill carried the same warning).
  const getPhaseInfo = () => {
    if (isThinking) {
      return { label: 'Reasoning', icon: Brain, ring: 'bg-clinical-cyan/30' }
    }
    if (hasActiveToolCall) {
      return { label: 'Working', icon: Wrench, ring: 'bg-clinical-amber/30' }
    }
    if (hasStreamingContent) {
      return { label: 'Writing', icon: Pencil, ring: 'bg-clinical-sage/30' }
    }
    return { label: 'Preparing', icon: Sparkles, ring: 'bg-primary/30' }
  }

  const phaseInfo = getPhaseInfo()
  const PhaseIcon = phaseInfo.icon

  return (
    <div className="flex flex-col animate-fade-in">
      {/* Main working indicator - matches assistant message layout */}
      <div className="flex gap-3">
        {/* Avatar - matches assistant message avatar */}
        <div className="relative flex-shrink-0">
          {/* Pulse ring animation */}
          <div className={cn(
            "absolute inset-0 rounded-xl opacity-40",
            phaseInfo.ring,
            "animate-ping"
          )} style={{ animationDuration: '2s' }} />

          {/* Avatar container */}
          <div className={cn(
            "relative w-8 h-8 rounded-xl",
            "bg-surface-elevated border border-border/50",
            "flex items-center justify-center",
            "transition-colors duration-300"
          )}>
            <Sparkles className={cn(
              "h-4 w-4 transition-colors duration-300",
              isThinking ? "text-clinical-cyan" :
              hasActiveToolCall ? "text-clinical-amber" :
              "text-primary"
            )} />
          </div>
        </div>

        {/* Content area */}
        <div className="flex-1 min-w-0 space-y-2">
          {/* Status card */}
          <div className={cn(
            "inline-flex items-center gap-3 px-4 py-2.5 rounded-xl",
            "bg-surface border border-border/50",
            "transition-all duration-300"
          )}>
            {/* Phase icon with animation */}
            <div className={cn(
              "relative flex items-center justify-center w-7 h-7 rounded-lg",
              "transition-colors duration-300",
              isThinking && "bg-clinical-cyan/10",
              hasActiveToolCall && "bg-clinical-amber/10",
              !isThinking && !hasActiveToolCall && "bg-primary/10"
            )}>
              <PhaseIcon className={cn(
                "h-4 w-4 transition-all duration-300",
                isThinking && "text-clinical-cyan animate-pulse",
                hasActiveToolCall && "text-clinical-amber",
                !isThinking && !hasActiveToolCall && "text-primary"
              )} />
            </div>

            {/* Neural wave visualization */}
            <div className="flex items-center gap-0.5 h-5">
              {waveHeights.map((height, i) => (
                <div
                  key={i}
                  className={cn(
                    "w-0.5 rounded-full transition-colors duration-300",
                    isThinking ? "bg-clinical-cyan/70" :
                    hasActiveToolCall ? "bg-clinical-amber/70" :
                    "bg-primary/70"
                  )}
                  style={{
                    height: `${height}px`,
                    animation: 'neural-wave 1.2s ease-in-out infinite',
                    animationDelay: `${i * 0.08}s`,
                  }}
                />
              ))}
            </div>

            {/* Phase label */}
            <span className={cn(
              "text-sm font-medium transition-colors duration-300",
              isThinking ? "text-clinical-cyan" :
              hasActiveToolCall ? "text-clinical-amber" :
              "text-foreground"
            )}>
              {phaseInfo.label}
            </span>
          </div>

          {/* Tool calls - rendered below the status card when present */}
          {hasToolCalls && (
            <div className="space-y-1 pl-1 animate-fade-in">
              {toolCalls.map((tc) => (
                <div
                  key={tc.id}
                  className={cn(
                    "flex items-center gap-2 text-sm",
                    "transition-all duration-200",
                    tc.status === 'running' && "text-foreground",
                    tc.status === 'complete' && "text-muted-foreground",
                    tc.status === 'error' && "text-destructive"
                  )}
                >
                  {/* Status indicator */}
                  <div className={cn(
                    "w-1.5 h-1.5 rounded-full flex-shrink-0",
                    "transition-colors duration-200",
                    tc.status === 'running' && "bg-clinical-amber animate-pulse",
                    tc.status === 'complete' && "bg-clinical-sage",
                    tc.status === 'error' && "bg-destructive"
                  )} />

                  {/* Completion icon for finished tools */}
                  {tc.status === 'complete' && (
                    <CheckCircle2 className="h-3 w-3 flex-shrink-0 text-clinical-sage" />
                  )}
                  {tc.status === 'error' && (
                    <AlertCircle className="h-3 w-3 flex-shrink-0 text-destructive" />
                  )}

                  {/* Tool name */}
                  <span className={cn(
                    "font-medium",
                    tc.status === 'running' && "text-foreground",
                    tc.status === 'complete' && "text-muted-foreground"
                  )}>
                    {tc.displayName}
                  </span>

                  {/* Query/skill preview */}
                  {(tc.args?.query || tc.args?.skill_name) && (
                    <span className="text-muted-foreground/50 truncate max-w-[180px] text-xs">
                      "{tc.args.skill_name || tc.args.query}"
                    </span>
                  )}

                  {/* Citation count badge */}
                  {tc.status === 'complete' && tc.citationCount !== undefined && tc.citationCount > 0 && (
                    <Badge
                      variant="secondary"
                      className="text-[10px] h-4 px-1.5 bg-clinical-sage/10 text-clinical-sage border-0"
                    >
                      {tc.citationCount} found
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Context-driven variant for the regular chat: sources the streaming state from the
 * global ChatContext and renders the shared view. Must be used inside a ChatProvider
 * (that is the regular chat's environment). The case panel renders
 * StreamingProgressPanelView directly with its per-agent data instead.
 */
export const StreamingProgressPanel: FC = () => {
  const ctx = useChat()
  return (
    <StreamingProgressPanelView
      toolCalls={ctx.streamingProgress.toolCalls}
      isThinking={ctx.streamingProgress.isThinking}
      isGenerating={ctx.isGenerating}
      isStreaming={ctx.isStreaming}
    />
  )
}
